/**
 * Subscription service for API communication.
 * 
 * Handles all subscription-related API calls including plans, billing, and usage.
 */

import {
  SubscriptionPlan,
  CurrentSubscription,
  FeatureAccess,
  CheckoutSession,
  AccessCheck,
  BillingPortalSession,
  UsageInfo,
  SubscriptionResponse,
  ClientConfig,
  SurveyChatTarget
} from '../../types/subscription';

const API_URL = import.meta.env.VITE_API_URL || '';

class SubscriptionService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_URL}/api/subscription`;
  }

  /**
   * Make an authenticated API request
   */
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<SubscriptionResponse<T>> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.detail || `HTTP ${response.status}: ${response.statusText}`,
          details: data.details || null
        };
      }

      return {
        success: true,
        data: data.success ? data : data,
        message: data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error occurred'
      };
    }
  }

  /**
   * Get all available subscription plans
   */
  async getAvailablePlans(): Promise<SubscriptionResponse<SubscriptionPlan[]>> {
    const response = await this.makeRequest<{ plans: SubscriptionPlan[] }>('/plans/');
    
    if (response.success && response.data) {
      return {
        success: true,
        data: response.data.plans || response.data
      };
    }
    
    return response as SubscriptionResponse<SubscriptionPlan[]>;
  }

  /**
   * Get current user's subscription details
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse<{
    currentPlan?: CurrentSubscription;
    features?: FeatureAccess;
    surfaces?: string[] | string;
    allowedUseCases?: string[] | string;
    autoSelectSingleSegment?: boolean;
    clientConfig?: ClientConfig | null;
    surveyChatTargets?: SurveyChatTarget[];
    data?: {
      currentPlan?: CurrentSubscription;
      features?: FeatureAccess;
      surfaces?: string[] | string;
      allowedUseCases?: string[] | string;
      autoSelectSingleSegment?: boolean;
      clientConfig?: ClientConfig | null;
      surveyChatTargets?: SurveyChatTarget[];
    };
  }>> {
    return this.makeRequest('/plans/current');
  }

  /**
   * Get user's feature access information
   */
  async getFeatureAccess(): Promise<SubscriptionResponse<FeatureAccess>> {
    return this.makeRequest('/plans/features');
  }

  /**
   * Check access to a specific use case
   */
  async checkUseCaseAccess(useCase: string): Promise<SubscriptionResponse<{ access: AccessCheck }>> {
    return this.makeRequest(`/plans/check-access/${encodeURIComponent(useCase)}`);
  }

  /**
   * Create a Stripe checkout session
   */
  async createCheckoutSession(
    planId: string,
    successUrl: string,
    cancelUrl: string
  ): Promise<SubscriptionResponse<CheckoutSession>> {
    return this.makeRequest('/checkout/create-session', {
      method: 'POST',
      body: JSON.stringify({
        plan_id: planId,
        success_url: successUrl,
        cancel_url: cancelUrl
      })
    });
  }

  /**
   * Get checkout session status
   */
  async getCheckoutSessionStatus(sessionId: string): Promise<SubscriptionResponse<any>> {
    return this.makeRequest(`/checkout/session/${sessionId}`);
  }

  /**
   * Handle successful checkout completion
   */
  async handleCheckoutSuccess(
    sessionId: string,
    subscriptionId?: string
  ): Promise<SubscriptionResponse<{ subscription: any }>> {
    return this.makeRequest('/checkout/success', {
      method: 'POST',
      body: JSON.stringify({
        session_id: sessionId,
        subscription_id: subscriptionId
      })
    });
  }

  /**
   * Cancel current subscription
   */
  async cancelSubscription(atPeriodEnd: boolean = true): Promise<SubscriptionResponse<any>> {
    return this.makeRequest('/manage/cancel', {
      method: 'POST',
      body: JSON.stringify({
        at_period_end: atPeriodEnd
      })
    });
  }

  /**
   * Change subscription plan
   */
  async changePlan(newPlanId: string): Promise<SubscriptionResponse<any>> {
    return this.makeRequest('/manage/change-plan', {
      method: 'POST',
      body: JSON.stringify({
        new_plan_id: newPlanId
      })
    });
  }

  /**
   * Create billing portal session
   */
  async createBillingPortal(returnUrl: string): Promise<SubscriptionResponse<BillingPortalSession>> {
    return this.makeRequest('/manage/billing-portal', {
      method: 'POST',
      body: JSON.stringify({
        return_url: returnUrl
      })
    });
  }

  /**
   * Get detailed usage information
   */
  async getUsageDetails(): Promise<SubscriptionResponse<{ usage: UsageInfo }>> {
    return this.makeRequest('/manage/usage');
  }

  /**
   * Reactivate a cancelled subscription
   */
  async reactivateSubscription(): Promise<SubscriptionResponse<any>> {
    return this.makeRequest('/manage/reactivate', {
      method: 'POST'
    });
  }

  /**
   * Get upgrade recommendations based on current usage
   */
  async getUpgradeRecommendations(): Promise<SubscriptionResponse<{
    recommendations: {
      planId: string;
      reason: string;
      benefits: string[];
    }[]
  }>> {
    // This would be implemented based on usage patterns
    // For now, return a basic recommendation
    const currentSub = await this.getCurrentSubscription();
    
    if (!currentSub.success) {
      return currentSub as SubscriptionResponse<any>;
    }

    const mockRecommendations = [];
    
    if (currentSub.data?.currentPlan?.id === 'free') {
      mockRecommendations.push({
        planId: 'starter',
        reason: 'Increase simulation limits',
        benefits: ['50 simulations per month', 'Access to AB Testing', 'Priority support']
      });
    }

    return {
      success: true,
      data: { recommendations: mockRecommendations }
    };
  }
}

// Export singleton instance
export const subscriptionService = new SubscriptionService();
export default subscriptionService;